import type { Tenant } from '../models/Tenant'

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RetorikTenantInfo } from '../models/RetorikTenantInfo'
import { tenants } from '../utils/tenantList'
import {
  LodgingBusinessPOI,
  POI,
  POIType,
  Place,
  ProduitPOI,
  RestaurantPOI,
  ServicePOI,
  TouristAttractionPOI,
  TouristDestinationPOI
} from '../models/POI'
import { generatePoiQuery } from '../utils/poiUtils'

const URL_API_TENANT = 'https://dev.management.retorik.ai/api/getAgents'
const URL_API_POI = 'https://studio.retorik.ai/'
const URL_TOKEN_API = 'https://auth.retorik.ai/oidc/token'
const API_CLIENT_ID = 'asklea'
const API_CLIENT_SERCRET = '388D45FA-B36B-4988-BA59-B187D329C207'

const getToken = async (): Promise<any> => {
  const token = (
    await fetch(URL_TOKEN_API, {
      method: 'POST',
      body: `grant_type=client_credentials&scope=profile&client_id=${API_CLIENT_ID}&client_secret=${API_CLIENT_SERCRET}`,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  ).json()
  return token
}

export const tenantApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: URL_API_TENANT,
    prepareHeaders: async (headers) => {
      const token = await getToken()
      console.log(token)
      // If we have a token set in state, let's assume that we should be passing it.
      if (token && token.access_token) {
        headers.set('authorization', `Bearer ${token.access_token}`)
      }

      return headers
    }
  }),
  tagTypes: [],
  endpoints: (builder) => ({
    getTenant: builder.query<{ [key: string]: Tenant }, void>({
      query: () => ``,
      transformResponse: (
        response: RetorikTenantInfo[]
      ): { [key: string]: Tenant } => {
        const res: { [key: string]: Tenant } = {}
        if (response && response.length > 0) {
          const filteredData = response.filter(
            (a: RetorikTenantInfo) =>
              a.showOnAskLea != null && a.showOnAskLea === true
          )
          filteredData.forEach((agent: any) => {
            res[agent.name] = {
              name: agent.name,
              retorikName: agent.name,
              areaLocation: agent.agentZone?.zone
            }
          })
        }
        return res
      },
      // Temporary return mock tenants from error
      transformErrorResponse: () => tenants
    })
  }),
  reducerPath: 'tenantApi'
})

export const poiApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: URL_API_POI,
    prepareHeaders: async (headers) => {
      const token = await getToken()

      // If we have a token set in state, let's assume that we should be passing it.
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }

      return headers
    }
  }),
  endpoints: (builder) => ({
    getListPOI: builder.query({
      queryFn: (tenant: string) => {
        const promises: Promise<POI[]>[] = []
        if (tenant && tenant.length > 0) {
          promises.push(fetchPoiType(POIType.afaire, tenant))
          promises.push(fetchPoiType(POIType.avoir, tenant))
          promises.push(fetchPoiType(POIType.hebergement, tenant))
          promises.push(fetchPoiType(POIType.produit, tenant))
          promises.push(fetchPoiType(POIType.restaurant, tenant))
          promises.push(fetchPoiType(POIType.service, tenant))
        }

        return Promise.all(promises).then((results) => {
          return { data: results.flat() } // final result must have either the property "data" or the property "error"
        })
      }
    })
  }),
  reducerPath: 'poiApi'
})

const getAddressFromPlaceData = (data: Place): string => {
  return `${data.streetAddress || ''} ${data.postalCode || ''} ${
    data.addressLocality || ''
  }`
}

const fetchPoiType = (type: POIType, tenant: string): Promise<POI[]> => {
  const urlPOIAPI = `${URL_API_POI}${tenant}/api/graphql`
  const response = fetch(urlPOIAPI, {
    method: 'POST',
    body: generatePoiQuery(type),
    headers: {
      'Content-type': 'application/graphql; charset=UTF-8'
    }
  })
    .then((response) => response.json())
    .then((data) => {
      const res: POI[] = []
      if (data && data.data) {
        if (
          type === POIType.restaurant &&
          data.data.foodEstablishment &&
          data.data.foodEstablishment.length > 0
        ) {
          data.data.foodEstablishment.forEach((e: RestaurantPOI) => {
            res.push({
              Location: { lat: e.place.latitude, lng: e.place.longitude },
              name: e.displayText,
              imageURL:
                e.thing.image?.urls?.length > 0
                  ? e.thing.image.urls[0]
                  : undefined,
              type: POIType.restaurant,
              description: e.thing.description,
              address: getAddressFromPlaceData(e.place),
              phone: e.place.telephone,
              contentItemId: e.contentItemId
            })
          })
        }
        if (
          type === POIType.hebergement &&
          data.data.lodgingBusiness &&
          data.data.lodgingBusiness.length > 0
        ) {
          data.data.lodgingBusiness.forEach((e: LodgingBusinessPOI) => {
            res.push({
              Location: { lat: e.place.latitude, lng: e.place.longitude },
              name: e.displayText,
              imageURL:
                e.thing.image?.urls?.length > 0
                  ? e.thing.image.urls[0]
                  : undefined,
              type: POIType.hebergement,
              description: e.thing.description,
              address: getAddressFromPlaceData(e.place),
              phone: e.place.telephone,
              contentItemId: e.contentItemId
            })
          })
        }
        if (
          type === POIType.service &&
          data.data.service &&
          data.data.service.length > 0
        ) {
          data.data.service.forEach((e: ServicePOI) => {
            let location
            if (e.place?.latitude && e.place?.longitude) {
              location = { lat: e.place?.latitude, lng: e.place?.longitude }
            }
            res.push({
              Location: location,
              name: e.displayText,
              imageURL:
                e.thing.image?.urls?.length > 0
                  ? e.thing.image.urls[0]
                  : undefined,
              type: POIType.service,
              contentItemId: e.contentItemId
            })
          })
        }
        if (
          type === POIType.afaire &&
          data.data.touristAttraction &&
          data.data.touristAttraction.length > 0
        ) {
          data.data.touristAttraction.forEach((e: TouristAttractionPOI) => {
            res.push({
              Location: { lat: e.place.latitude, lng: e.place.longitude },
              name: e.displayText,
              imageURL:
                e.thing.image?.urls?.length > 0
                  ? e.thing.image.urls[0]
                  : undefined,
              type: POIType.afaire,
              description: e.thing.description,
              address: getAddressFromPlaceData(e.place),
              phone: e.place.telephone,
              contentItemId: e.contentItemId
            })
          })
        }
        if (
          type === POIType.avoir &&
          data.data.touristDestination &&
          data.data.touristDestination.length > 0
        ) {
          data.data.touristDestination.forEach((e: TouristDestinationPOI) => {
            res.push({
              Location: { lat: e.place.latitude, lng: e.place.longitude },
              name: e.displayText,
              imageURL:
                e.thing.image?.urls?.length > 0
                  ? e.thing.image.urls[0]
                  : undefined,
              type: POIType.avoir,
              description: e.thing.description,
              address: getAddressFromPlaceData(e.place),
              phone: e.place.telephone,
              contentItemId: e.contentItemId
            })
          })
        }

        if (
          type === POIType.produit &&
          data.data.product &&
          data.data.product.length > 0
        ) {
          data.data.product.forEach((e: ProduitPOI) => {
            let location
            if (e.place?.latitude && e.place?.longitude) {
              location = { lat: e.place?.latitude, lng: e.place?.longitude }
            }
            res.push({
              Location: location,
              name: e.displayText,
              imageURL:
                e.thing.image?.urls?.length > 0
                  ? e.thing.image.urls[0]
                  : undefined,
              type: POIType.produit,
              contentItemId: e.contentItemId
            })
          })
        }
      }

      return res
    })
  return response
}

// Export hooks for usage in functional components
export const { useGetTenantQuery } = tenantApi
export const { /* useGetPOIQuery */ useGetListPOIQuery } = poiApi
